import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';

interface ChartTooltipProps {
  /** posição de ancoragem (ponto / topo da barra), em % do container (0..100). */
  xPct: number;
  yPct: number;
  label: string;
  value: ReactNode;
  /** ponto colorido opcional (cor da série). */
  color?: string;
  className?: string;
}

/**
 * Leitura de hover padronizada dos gráficos (linha/barras): balão ancorado no
 * ponto. Usa surface+border (adapta a claro/escuro) e é pointer-events-none para
 * nunca roubar o hover do gráfico. O chamador só o renderiza quando há elemento
 * ativo (hover/foco). É `aria-hidden` — o próprio shape focado já anuncia o
 * mesmo rótulo+valor via aria-label, então o balão é só reforço visual.
 *
 * Fica dentro dos limites do card: clampa a horizontal (o centro nunca chega às
 * bordas) e, quando o ponto/barra está muito no topo, vira o balão para BAIXO da
 * âncora em vez de cobrir o título do card.
 */
export function ChartTooltip({ xPct, yPct, label, value, color, className }: ChartTooltipProps) {
  const clampedX = Math.min(Math.max(xPct, 12), 88);
  const below = yPct < 25;
  return (
    <div
      className={cn(
        'pointer-events-none absolute z-10 -translate-x-1/2 rounded-lg border border-border bg-surface px-2.5 py-1.5 text-center shadow-hover',
        below ? 'translate-y-0' : '-translate-y-full',
        className,
      )}
      style={{ left: `${clampedX}%`, top: below ? `calc(${yPct}% + 10px)` : `calc(${yPct}% - 8px)` }}
      aria-hidden
    >
      <div className="flex items-center justify-center gap-1.5 whitespace-nowrap">
        {color && (
          <span className="inline-block size-2 shrink-0 rounded-full" style={{ backgroundColor: color }} />
        )}
        <span className="text-[11px] font-medium text-muted">{label}</span>
      </div>
      <div className="whitespace-nowrap text-[13px] font-bold text-ink">{value}</div>
    </div>
  );
}
